import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { LoaderCircle, X } from 'lucide-react';
import CreateTicketForm from './CreateTicketForm';

const FORM_ID = 'create-ticket-modal-form';
const TITLE_ID = 'create-ticket-modal-title';
const DESCRIPTION_ID = 'create-ticket-modal-description';
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const dialogIn = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(10, 10, 10, 0.45);
  backdrop-filter: blur(2px);
  animation: ${fadeIn} 0.16s ease-out;

  @media (max-width: 640px) {
    padding: 0;
    align-items: stretch;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Dialog = styled.div`
  display: flex;
  flex-direction: column;
  width: min(640px, calc(100vw - 2rem));
  max-height: 90vh;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 14px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.22), 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  animation: ${dialogIn} 0.18s cubic-bezier(0.2, 0.8, 0.2, 1);

  &:focus {
    outline: none;
  }

  @media (max-width: 640px) {
    width: 100%;
    max-height: none;
    height: 100dvh;
    border: none;
    border-radius: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid var(--color-border-primary);
  flex-shrink: 0;

  @media (max-width: 640px) {
    padding: 1rem 1.125rem 0.875rem;
  }
`;

const HeaderTexts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 650;
  letter-spacing: -0.015em;
  line-height: 1.3;
  color: var(--color-text-primary);
`;

const Description = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--color-text-secondary);
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: -0.25rem -0.5rem 0 0;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--color-hover-overlay);
    color: var(--color-text-primary);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 1.25rem 1.5rem 1.5rem;

  @media (max-width: 640px) {
    padding: 1rem 1.125rem 1.25rem;
  }
`;

const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.875rem 1.5rem;
  border-top: 1px solid var(--color-border-primary);
  background: var(--color-bg-secondary);
  flex-shrink: 0;

  @media (max-width: 640px) {
    padding: 0.75rem 1.125rem calc(0.75rem + env(safe-area-inset-bottom));
  }
`;

const FooterText = styled.p`
  margin: 0;
  min-width: 0;
  font-size: 0.8125rem;
  color: ${({ $strong }) => ($strong ? 'var(--color-text-primary)' : 'var(--color-text-muted)')};
  font-weight: ${({ $strong }) => ($strong ? 500 : 400)};

  kbd {
    display: inline-block;
    padding: 0 0.3rem;
    border: 1px solid var(--color-border-secondary);
    border-bottom-width: 2px;
    border-radius: 4px;
    font-family: inherit;
    font-size: 0.6875rem;
    line-height: 1.4;
    color: var(--color-text-secondary);
    background: var(--color-bg-primary);
  }

  @media (max-width: 640px) {
    display: ${({ $strong }) => ($strong ? 'block' : 'none')};
  }
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 36px;
  padding: 0 0.875rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: opacity 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
    transform 0.1s ease;

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  ${buttonBase}
  border: 1px solid var(--color-border-secondary);
  background: var(--color-bg-primary);
  color: var(--color-text-primary);

  &:hover:not(:disabled) {
    background: var(--color-bg-tertiary);
  }
`;

const PrimaryButton = styled.button`
  ${buttonBase}
  min-width: 120px;
  border: 1px solid var(--color-btn-primary-border);
  background: var(--color-btn-primary-bg);
  color: var(--color-btn-primary-text);

  &:hover:not(:disabled) {
    opacity: 0.88;
  }
`;

const DangerButton = styled.button`
  ${buttonBase}
  border: 1px solid rgba(220, 53, 69, 0.35);
  background: rgba(220, 53, 69, 0.08);
  color: #dc3545;

  &:hover:not(:disabled) {
    background: rgba(220, 53, 69, 0.14);
  }
`;

const Spinner = styled(LoaderCircle)`
  animation: ${spin} 0.8s linear infinite;
`;

const CreateTicketModalContent = ({ onClose, onCreated }) => {
  const dialogRef = useRef(null);
  const [dirty, setDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const requestClose = useCallback(() => {
    if (submitting) return;
    if (dirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  }, [dirty, submitting, onClose]);

  useEffect(() => {
    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    if (!dirty) setConfirmDiscard(false);
  }, [dirty]);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      if (confirmDiscard) {
        setConfirmDiscard(false);
        return;
      }
      requestClose();
      return;
    }

    if (event.key !== 'Tab' || !dialogRef.current) return;
    const focusable = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleSuccess = useCallback(
    (created) => {
      onCreated(created);
      onClose();
    },
    [onCreated, onClose]
  );

  return (
    <Backdrop
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        aria-describedby={DESCRIPTION_ID}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <Header>
          <HeaderTexts>
            <Title id={TITLE_ID}>Novo ticket</Title>
            <Description id={DESCRIPTION_ID}>
              Descreva o problema ou a solicitação. A equipe de suporte acompanha a partir daqui.
            </Description>
          </HeaderTexts>
          <CloseButton
            type="button"
            onClick={requestClose}
            disabled={submitting}
            aria-label="Fechar"
            title="Fechar (Esc)"
          >
            <X size={18} strokeWidth={2} aria-hidden />
          </CloseButton>
        </Header>

        <Body>
          <CreateTicketForm
            formId={FORM_ID}
            onSuccess={handleSuccess}
            onDirtyChange={setDirty}
            onSubmittingChange={setSubmitting}
          />
        </Body>

        <Footer>
          {confirmDiscard ? (
            <>
              <FooterText $strong role="alert">
                Descartar este ticket? O que você preencheu será perdido.
              </FooterText>
              <FooterActions>
                <SecondaryButton type="button" onClick={() => setConfirmDiscard(false)} autoFocus>
                  Continuar editando
                </SecondaryButton>
                <DangerButton type="button" onClick={onClose}>
                  Descartar
                </DangerButton>
              </FooterActions>
            </>
          ) : (
            <>
              <FooterText>
                <kbd>Esc</kbd> para fechar
              </FooterText>
              <FooterActions>
                <SecondaryButton type="button" onClick={requestClose} disabled={submitting}>
                  Cancelar
                </SecondaryButton>
                <PrimaryButton type="submit" form={FORM_ID} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Spinner size={16} strokeWidth={2.25} aria-hidden />
                      Criando...
                    </>
                  ) : (
                    'Criar ticket'
                  )}
                </PrimaryButton>
              </FooterActions>
            </>
          )}
        </Footer>
      </Dialog>
    </Backdrop>
  );
};

const CreateTicketModal = ({ open, onClose, onCreated }) => {
  if (!open || typeof document === 'undefined') return null;
  return createPortal(<CreateTicketModalContent onClose={onClose} onCreated={onCreated} />, document.body);
};

export default CreateTicketModal;
